import React from "react";
import ReactSelect from "./ReactSelect"
import BoolFilter from "@inovua/reactdatagrid-community/BoolFilter";
import DateFilter from "@inovua/reactdatagrid-community/DateFilter";
import NumberFilter from '@inovua/reactdatagrid-community/NumberFilter'
import DateEditor from '@inovua/reactdatagrid-community/DateEditor';
import BoolEditor from '@inovua/reactdatagrid-community/BoolEditor';
import SelectEditor from '@inovua/reactdatagrid-community/SelectEditor'
import NumericEditor from '@inovua/reactdatagrid-community/NumericEditor';

const ReactSelectEditor = (editorProps, options, multi) => {
  return (
    <div style={{ width: '100%' }}>
      <ReactSelect
        id={editorProps.value}
        multi={multi}
        options={options}
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        onBlur={() => editorProps.onComplete()}
        customChangeHandler={(e) => {
          if (multi) {
            editorProps.onChange(e.map((v) => v.value));
          } else {
            editorProps.onChange(e.value);
          }
        }}
      />
    </div>
  );
};

const searchTermLayout = (props) => {
  const columns = [
    {
      name: "id",
      header: "Id",
      type: "number",
      defaultVisible: false,
      editable: false,
    },
    {
      name: "term",
      header: "Term",
      defaultFlex: 1,
      sortable: true,
    },
    {
      name: "branded",
      header: "Branded",
      render: ({ value }) => value ? "yes" : "no",
      editor: BoolEditor,
      filterEditor: BoolFilter,
      sortable: false,
    },
    {
      name: "show_legacy_config",
      header: "Show Legacy Config",
      render: ({ value }) => value ? "yes" : "no",
      editor: BoolEditor,
      filterEditor: BoolFilter,
      sortable: false,
    },
    {
      name: "updated_at",
      header: "Last Updated",
      defaultFlex: 2,
      render: ({ value }) => <span>{new Date(value).toLocaleString()}</span>,
      sortable: true,
      resizable: true,
      editable: false,
    }
  ];

  const filterValue = [
    { name: 'term', operator: 'contains', type: 'string', value: '' },
    { name: 'branded', operator: 'eq', type: 'bool', value: null },
    { name: 'show_legacy_config', operator: 'eq', type: 'bool', value: null },
  ];

  const defaultSortInfo = { name: 'term', dir: 1 }

  return { columns, filterValue, defaultSortInfo };
}

const weightsTargetsLayout = (props) => {
  const withBlank = (options = []) => [{ label: " ", id: null }, ...options];

  const columns = [
    {
      name: "id",
      header: "Id",
      type: "number",
      defaultVisible: false,
      editable: false,
    },
    {
      name: "retailer",
      header: "Retailer",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editable: (value, cellProps) => cellProps.data.isNewRow,
      editorProps: {
        idProperty: 'id',
        dataSource: withBlank(props.options.retailers),
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
    {
      name: "brand",
      header: "Brand",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editable: (value, cellProps) => cellProps.data.isNewRow,
      editorProps: {
        idProperty: 'id',
        dataSource: withBlank(props.options.brands),
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
    {
      name: "measure_config",
      header: "Measure",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editable: (value, cellProps) => cellProps.data.isNewRow,
      editorProps: {
        idProperty: 'id',
        dataSource: withBlank(props.options.measure_configs),
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
    {
      name: "measure_group",
      header: "Measure Group",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editable: (value, cellProps) => cellProps.data.isNewRow,
      editorProps: {
        idProperty: 'id',
        dataSource: withBlank(props.options.measure_groups),
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
    {
      name: "weight",
      header: "Weight (%)",
      defaultFlex: 1,
      editor: NumericEditor,
      sortable: true,
    },
    {
      name: "target",
      header: "Target (%)",
      defaultFlex: 1,
      editor: NumericEditor,
      sortable: true,
    },
    {
      name: "scorecard",
      header: "Scorecard",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editorProps: {
        idProperty: 'id',
        dataSource: props.options.scorecards,
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
  ];

  const filterValue = [
    { name: 'retailer', operator: 'contains', type: 'string', value: '' },
    { name: 'brand', operator: 'contains', type: 'string', value: '' },
    { name: 'measure_config', operator: 'contains', type: 'string', value: '' },
    { name: 'measure_group', operator: 'contains', type: 'string', value: '' },
    { name: 'scorecard', operator: 'contains', type: 'string', value: '' }
  ];

  const defaultSortInfo = { name: "retailer", dir: 1 }

  return { columns, filterValue, defaultSortInfo };
}

const manualMeasuresLayout = (props) => {
  const withBlank = (options = []) => [{ label: " ", id: null }, ...options];

  const columns = [
    {
      name: "id",
      header: "Id",
      type: "number",
      defaultVisible: false,
      editable: false,
    },
    {
      name: "date",
      header: "Date",
      defaultFlex: 1,
      filterEditor: DateFilter,
      editor: DateEditor,
      filterEditorProps: (props, { index }) => {
        // for range and notinrange operators, the index is 1 for the after field
        return {
          dateFormat: 'YYYY-MM-DD',
          cancelButton: false,
          highlightWeekends: false
        }
      },
      sortable: true,
    },
    {
      name: "retailer",
      header: "Retailer",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editorProps: {
        idProperty: 'id',
        dataSource: withBlank(props.options.retailers),
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
    {
      name: "brand",
      header: "Brand",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editorProps: {
        idProperty: 'id',
        dataSource: withBlank(props.options.brands),
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
    {
      name: "category",
      header: "Category",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editorProps: {
        idProperty: 'id',
        dataSource: withBlank(props.options.categories),
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
    {
      name: "score",
      header: "Score",
      defaultFlex: 1,
      editor: NumericEditor,
      filterEditor: NumberFilter,
      sortable: true,
    },
    {
      name: "maximum_score",
      header: "Max Score",
      defaultFlex: 1,
      editor: NumericEditor,
      filterEditor: NumberFilter,
      sortable: true,
    },
  ];

  const filterValue = [
    { name: 'date', operator: 'eq', type: 'date', value: '' },
    { name: 'retailer', operator: 'contains', type: 'string', value: '' },
    { name: 'category', operator: 'contains', type: 'string', value: '' },
    { name: 'brand', operator: 'contains', type: 'string', value: '' },
    { name: 'score', operator: 'eq', type: 'number', value: null },
    { name: 'maximum_score', operator: 'eq', type: 'number', value: null }
  ];

  const defaultSortInfo = { name: "retailer", dir: 1 }

  return { columns, filterValue, defaultSortInfo };
}

const searchTermWeightsLayout = (props) => {
  const withBlank = (options = []) => [{ label: " ", id: null }, ...options];

  const columns = [
    {
      name: "id",
      header: "Id",
      type: "number",
      defaultVisible: false,
      editable: false,
    },
    {
      name: "search_term",
      header: "Search Term",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editable: (value, cellProps) => cellProps.data.isNewRow,
      editorProps: {
        idProperty: 'id',
        dataSource: withBlank(props.options.search_terms),
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
    {
      name: "weight",
      header: "Weight (%)",
      defaultFlex: 1,
      editor: NumericEditor,
      sortable: true,
    },
    {
      name: "scorecard",
      header: "Scorecard",
      defaultFlex: 1,
      sortable: true,
      editor: SelectEditor,
      editorProps: {
        idProperty: 'id',
        dataSource: props.options.scorecards,
        collapseOnSelect: true,
        clearIcon: null
      },
      enableColumnFilterContextMenu: false
    },
  ];

  const filterValue = [
    { name: 'search_term', operator: 'contains', type: 'string', value: '' },
    { name: 'scorecard', operator: 'contains', type: 'string', value: '' }
  ];

  const defaultSortInfo = { name: "search_term", dir: 1 }

  return { columns, filterValue, defaultSortInfo };
}

const disabledCapabilitiesLayout = (props) => {
  const columns = [
    {
      name: "id",
      header: "Id",
      type: "number",
      defaultVisible: false,
      editable: false,
    },
    {
      name: "retailer",
      header: "Retailer",
      editor: SelectEditor,
      editorProps: {
        idProperty: 'id',
        dataSource: props.options.retailers,
        collapseOnSelect: true,
        clearIcon: null
      },
      defaultFlex: 1,
      sortable: true,
    },
    {
      name: "retailer_capability",
      header: "Retailer Capability",
      editor: SelectEditor,
      editorProps: {
        idProperty: 'id',
        dataSource: props.options.retailer_capabilities,
        collapseOnSelect: true,
        clearIcon: null
      },
      defaultFlex: 2,
      sortable: true,
    },
    {
      name: "date_from",
      header: "Date From",
      render: ({ value }) => <span>{value && new Date(value).toISOString().split('T')[0]}</span>,
      filterEditor: DateFilter,
      filterEditorProps: (props, { index }) => {
        // for range and notinrange operators, the index is 1 for the after field
        return {
          dateFormat: 'DD-MM-YYYY',
          cancelButton: false,
          highlightWeekends: false,
          placeholder: index == 1 ? 'Created date is before...' : 'Created date is after...'
        }
      },
      editor: DateEditor,
      defaultFlex: 1,
      sortable: true,
    },
    {
      name: "date_to",
      header: "Date To",
      render: ({ value }) => <span>{value && new Date(value).toISOString().split('T')[0]}</span>,
      filterEditor: DateFilter,
      filterEditorProps: (props, { index }) => {
        // for range and notinrange operators, the index is 1 for the after field
        return {
          dateFormat: 'DD-MM-YYYY',
          cancelButton: false,
          highlightWeekends: false,
          placeholder: index == 1 ? 'Created date is before...' : 'Created date is after...'
        }
      },
      editor: DateEditor,
      defaultFlex: 1,
      sortable: true,
    },
    {
      name: "source",
      header: "Source",
      defaultFlex: 1,
      editable: false,
      sortable: true,
    },
    {
      name: "author",
      header: "Author",
      defaultFlex: 2,
      editable: false,
      sortable: true,
    },
    {
      name: "created_at",
      header: "Created",
      defaultFlex: 1,
      render: ({ value }) => <span>{new Date(value).toLocaleString()}</span>,
      sortable: true,
      resizable: true,
      editable: false,
    },
    {
      name: "updated_at",
      header: "Last Updated",
      defaultFlex: 1,
      render: ({ value }) => <span>{new Date(value).toLocaleString()}</span>,
      sortable: true,
      resizable: true,
      editable: false,
    }
  ];

  const filterValue = [
    { name: 'retailer', operator: 'contains', type: 'string', value: '' },
    { name: 'retailer_capability', operator: 'contains', type: 'string', value: '' },
    { name: 'date_from', operator: 'before', type: 'date', value: '' },
    { name: 'date_to', operator: 'before', type: 'date', value: '' },
    { name: 'retailer_capability', operator: 'contains', type: 'string', value: '' },
    { name: 'source', operator: 'contains', type: 'string', value: '' },
    { name: 'author', operator: 'contains', type: 'string', value: '' },
  ];

  const defaultSortInfo = { name: 'retailer', dir: 1 }

  return { columns, filterValue, defaultSortInfo };
}

const retailersLayout = (props) => {
  const columns = [
    {
      name: "id",
      header: "Id",
      type: "number",
      defaultVisible: false,
      editable: false,
    },
    {
      name: "buffered",
      header: "Retailer",
      csvName: "retailer",
      defaultFlex: 1,
      sortable: true,
      renderEditor: (editorProps) => ReactSelectEditor(editorProps, props.options.buffered),
      enableColumnFilterContextMenu: false
    },
    {
      name: "channels",
      header: "Channels",
      defaultFlex: 1,
      sortable: true,
      type: "array",
      render: ({ value }) => value ? value.join(', ') : "",
      renderEditor: (editorProps) => ReactSelectEditor(editorProps, props.options.channels, true),
      enableColumnFilterContextMenu: false
    },
    {
      name: "revenue_share",
      header: "Revenue Share (%)",
      defaultFlex: 1,
      type: "number",
      editor: NumericEditor,
      filterEditor: NumberFilter,
      showFooter: true,
      sortable: true,
    },
    {
      name: "coverage",
      header: "Coverage (%)",
      defaultFlex: 1,
      type: "number",
      editor: NumericEditor,
      filterEditor: NumberFilter,
      showFooter: true,
      sortable: true,
    }
  ];

  const filterValue = [
    { name: 'buffered', operator: 'contains', type: 'string', value: '' },
    { name: 'channels', operator: 'contains', type: 'string', value: '' },
    { name: 'revenue_share', operator: 'eq', type: 'number', value: null, active: false },
    { name: 'coverage', operator: 'eq', type: 'number', value: null, active: false }
  ];

  const defaultSortInfo = { name: "buffered", dir: 1 }

  return { columns, filterValue, defaultSortInfo };
}

const brandsLayout = (props) => {
  const columns = [
    {
      name: "id",
      header: "Id",
      type: "number",
      defaultVisible: false,
      editable: false,
    },
    {
      name: "buffered",
      header: "Brand",
      csvName: "brand",
      defaultFlex: 1,
      sortable: true,
      renderEditor: (editorProps) => ReactSelectEditor(editorProps, props.options.buffered),
      enableColumnFilterContextMenu: false
    },
    {
      name: "mybrand",
      header: "My Brand",
      render: ({ value }) => value ? "yes" : "no",
      editor: BoolEditor,
      filterEditor: BoolFilter,
      sortable: true,
    },
    {
      name: "order",
      header: "Order",
      defaultFlex: 1,
      type: "number",
      editor: NumericEditor,
      filterEditor: NumberFilter,
      sortable: true,
    }
  ];

  const filterValue = [
    { name: 'buffered', operator: 'contains', type: 'string', value: '' },
    { name: 'mybrand', operator: 'eq', type: 'bool', value: null },
    { name: 'order', operator: 'eq', type: 'number', value: null, active: false }
  ];

  const defaultSortInfo = { name: "buffered", dir: 1 }

  return { columns, filterValue, defaultSortInfo };
}

const categoriesLayout = (props) => {
  const columns = [
    {
      name: "id",
      header: "Id",
      type: "number",
      defaultVisible: false,
      editable: false,
    },
    {
      name: "buffered",
      header: "Category",
      csvName: "category",
      defaultFlex: 1,
      sortable: true,
      renderEditor: (editorProps) => ReactSelectEditor(editorProps, props.options.buffered),
      enableColumnFilterContextMenu: false
    }
  ];

  const filterValue = [
    { name: 'buffered', operator: 'contains', type: 'string', value: '' }
  ];

  const defaultSortInfo = { name: "buffered", dir: 1 }

  return { columns, filterValue, defaultSortInfo };
}

const getLayout = (layoutName, props) => {
  switch (layoutName) {
    case "search_terms":
      return searchTermLayout(props);
    case "weights_targets":
      return weightsTargetsLayout(props);
    case "manual_measures":
      return manualMeasuresLayout(props);
    case "search_term_weights":
      return searchTermWeightsLayout(props);
    case "disabled_capabilities":
      return disabledCapabilitiesLayout(props);
    case "retailers":
      return retailersLayout(props);
    case "brands":
      return brandsLayout(props);
    case "categories":
      return categoriesLayout(props);
    default:
      return { columns: [], filterValue: [] };
  }
};

export default getLayout;